/**
 * PhoneFrame.tsx
 *
 * Generic Android phone UI frame with Material Design 3 styling.
 * Displays a 360x800 phone screen with status bar, navigation bar, and app content area.
 *
 * Features:
 * - Material Design 3 styling
 * - Real-time network status (operator, technology, signal)
 * - Status bar with time and battery
 * - Navigation bar with Android buttons
 * - Rounded corners (16px)
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Battery, Wifi, Signal, ArrowLeft, Circle, Square } from 'lucide-react';
import type { PhoneNetworkStatusUpdate } from '@/services/phoneWebSocketService';

export interface PhoneNavHandlers {
  /** Android back gesture — in-app back (exit to launcher by default). */
  onBack?: () => void;
  /** Home button — always returns to launcher. */
  onHome?: () => void;
  /** Recents button — opens app switcher / returns to launcher. */
  onRecents?: () => void;
}

export interface PhoneFrameProps {
  /** Network status from WebSocket */
  networkStatus?: PhoneNetworkStatusUpdate;
  /** Current app being displayed */
  children: React.ReactNode;
  /** Optional className for container */
  className?: string;
  /** Registration overlay message */
  registrationMessage?: string;
  /** Whether phone is registered on network */
  isRegistered?: boolean;
  /** Whether the cellular data session (bearer) is still being opened. Keeps
   *  the full-screen loading overlay up *after* registration until the data
   *  plane is warm, so the user can't start browsing before the first page can
   *  actually load. */
  dataPreparing?: boolean;
  /** Overlay message shown while the data session is being opened. */
  dataMessage?: string;
  /** Custom width (default: 360) */
  width?: number;
  /** Custom height (default: 800) */
  height?: number;
  /** Hide the in-frame status bar (operator + signal + battery). Use when the
   *  caller is already showing those values in an external header strip. */
  hideStatusBar?: boolean;
  /** Wire the OS-style nav bar buttons to real actions. Handlers that are
   *  undefined render their button in a muted/disabled style so users get
   *  visual feedback that the control is inactive (e.g. at the launcher). */
  navHandlers?: PhoneNavHandlers;
}

/**
 * PhoneStatusBar Component
 * Displays time, network info, signal bars, and battery
 */
interface PhoneStatusBarProps {
  networkStatus?: PhoneNetworkStatusUpdate;
  time: string;
}

const PhoneStatusBar: React.FC<PhoneStatusBarProps> = ({ networkStatus, time }) => {
  // signalQuality is a percentage (0-100) produced by phoneWebSocketService's
  // live Firestore snapshot. Map into 0..4 bars.
  const signalBars = typeof networkStatus?.signalQuality === 'number'
    ? Math.max(0, Math.min(4, Math.ceil(networkStatus.signalQuality / 25)))
    : 0;

  return (
    <div className="flex justify-between items-center h-6 px-3 bg-background text-foreground text-xs font-medium">
      {/* Left: Time */}
      <span>{time}</span>

      {/* Right: Network + Signal + Battery */}
      <div className="flex items-center gap-2">
        {!networkStatus?.registered ? (
          <span className="text-muted-foreground">Searching...</span>
        ) : (
          <>
            {/* Operator name */}
            <span className="text-foreground">{networkStatus.operator}</span>

            {/* Network technology badge. When the modem is on LTE but the
                cell broadcasts NSA + UE is admitted (tlamNsa.fiveGIcon=1),
                upgrade the badge to "5G" — same SIB-driven logic Quectel
                firmware uses on phones. "5G+" when NR is actively aggregating.
                "LTE" otherwise. */}
            {(() => {
              const nsa = networkStatus.nsaState;
              if (nsa === 'nsa_aggregating') {
                return (
                  <span
                    className="px-1 rounded text-[10px] font-bold bg-tech-5g-bg text-tech-5g"
                    title="NR is aggregating with LTE (QENDC + QCAINFO confirmed)"
                  >
                    5G+
                  </span>
                );
              }
              if (nsa === 'nsa_available') {
                return (
                  <span
                    className="px-1 rounded text-[10px] font-bold bg-tech-5g-bg text-tech-5g"
                    title="Cell broadcasts NSA, UE is admitted (QENDC field 4 = 1)"
                  >
                    5G
                  </span>
                );
              }
              return (
                <span
                  className={cn(
                    'px-1 rounded text-[10px] font-bold',
                    networkStatus.technology === '5G' && 'bg-tech-5g-bg text-tech-5g',
                    networkStatus.technology === '4G LTE' && 'bg-tech-4g-bg text-tech-4g',
                    networkStatus.technology === '3G' && 'bg-tech-3g-bg text-tech-3g',
                    networkStatus.technology === '2G' && 'bg-tech-2g-bg text-tech-2g'
                  )}
                  title={
                    nsa === 'endc_capable_no_attach'
                      ? networkStatus.endcRestrict === 1
                        ? 'Cell supports NSA but network restricts this UE (RestrictDCNR)'
                        : 'Cell supports NSA but UE not currently admitted'
                      : undefined
                  }
                >
                  {networkStatus.technology}
                </span>
              );
            })()}

            {/* Signal bars */}
            <div className="flex items-end gap-[2px] h-3">
              {[1, 2, 3, 4].map((bar) => (
                <div
                  key={bar}
                  className={cn(
                    'w-[3px] rounded-sm',
                    bar <= signalBars ? 'bg-foreground' : 'bg-muted'
                  )}
                  style={{ height: `${bar * 25}%` }}
                />
              ))}
            </div>
          </>
        )}

        {/* Battery icon */}
        <Battery className="w-4 h-4 text-foreground" />
      </div>
    </div>
  );
};

/**
 * PhoneNavigationBar Component
 * Android navigation bar with back, home, and recents buttons. Handlers that
 * are undefined render the button muted so users see which controls are live.
 */
const PhoneNavigationBar: React.FC<{ handlers?: PhoneNavHandlers }> = ({ handlers = {} }) => {
  const { onBack, onHome, onRecents } = handlers;
  const btn = (active: boolean) =>
    cn(
      'p-2 rounded-full transition-colors',
      active ? 'hover:bg-muted text-foreground' : 'text-muted-foreground opacity-40 cursor-not-allowed'
    );
  return (
    <div className="flex justify-around items-center h-12 bg-background border-t border-border">
      <button onClick={onBack} disabled={!onBack} className={btn(!!onBack)} title="Back">
        <ArrowLeft className="w-6 h-6" />
      </button>
      <button onClick={onHome} disabled={!onHome} className={btn(!!onHome)} title="Home">
        <Circle className="w-6 h-6" />
      </button>
      <button onClick={onRecents} disabled={!onRecents} className={btn(!!onRecents)} title="Recents">
        <Square className="w-6 h-6" />
      </button>
    </div>
  );
};

/**
 * PhoneFrame Component
 * Main container that renders the generic Android phone UI
 */
export const PhoneFrame: React.FC<PhoneFrameProps> = ({
  networkStatus,
  children,
  className,
  registrationMessage,
  isRegistered = false,
  dataPreparing = false,
  dataMessage,
  width = 360,
  height = 800,
  hideStatusBar = false,
  navHandlers,
}) => {
  const [currentTime, setCurrentTime] = useState<string>('');

  // Update time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      setCurrentTime(`${hours}:${minutes}`);
    };

    updateTime(); // Initial update
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Calculate scale for proportional sizing
  const scale = width / 360;
  const borderRadius = Math.round(16 * scale);

  return (
    <div
      className={cn(
        'relative flex flex-col bg-background border border-border shadow-2xl overflow-hidden',
        className
      )}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: `${borderRadius}px`
      }}
    >
      {/* Status Bar */}
      {!hideStatusBar && <PhoneStatusBar networkStatus={networkStatus} time={currentTime} />}

      {/* App Content Area */}
      <div className="flex-1 overflow-hidden relative">
        {/* Loading overlay — covers both phases: network registration, then
            opening the cellular data session. Stays up until the bearer is
            warm so the user can't start browsing before the first page can
            actually load. */}
        {(!isRegistered || dataPreparing) && (
          <div className="absolute inset-0 bg-background/95 flex items-center justify-center z-50">
            <div className="text-center px-6">
              <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-foreground text-sm">
                {!isRegistered
                  ? (registrationMessage || 'Connecting to network...')
                  : (dataMessage || 'Opening data session…')}
              </p>
            </div>
          </div>
        )}

        {/* App content */}
        {children}
      </div>

      {/* Navigation Bar */}
      <PhoneNavigationBar handlers={navHandlers} />
    </div>
  );
};

export default PhoneFrame;
